import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext.js';

const API = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hasIntro, setHasIntro] = useState(false);
  const [needTotp, setNeedTotp] = useState(false);
  const [totp, setTotp] = useState('');

  // Is an explainer video configured? (public endpoint, no auth)
  useEffect(() => {
    fetch(`${API}/api/intro/meta`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setHasIntro(Boolean(d?.available)))
      .catch(() => {});
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password, needTotp ? totp : undefined);
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'invalid_credentials';
      if (reason === 'totp_required') {
        setNeedTotp(true);
        setError(null);
      } else if (reason === 'invalid_totp') {
        setNeedTotp(true);
        setError('Invalid authentication code');
      } else {
        setError('Invalid credentials');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen grid place-items-center p-8">
      <div className="w-full max-w-sm space-y-7">
        {/* Explainer video — click to play, above the login */}
        {hasIntro && (
          <div className="space-y-2">
            <video
              controls
              preload="metadata"
              playsInline
              className="w-full rounded-2xl border border-ink/15 bg-black shadow-[0_0_40px] shadow-particle/10"
              src={`${API}/api/intro/video`}
            />
            <p className="text-center text-[11px] text-ink/35">
              ▶ Watch — what is martindaigneault.ai
            </p>
          </div>
        )}

      <form onSubmit={onSubmit} className="space-y-7">
        <div className="text-center space-y-3">
          <p className="text-[11.5px] text-amber/80">martindaigneault.ai</p>
          <h1 className="font-serif text-4xl text-ink leading-tight">
            Step into the <em className="italic text-particle">crystal</em>.
          </h1>
          <p className="font-serif italic text-ink/60 text-sm">Sign in to continue training the memory.</p>
        </div>

        <div className="space-y-3">
          <input
            type="email"
            placeholder="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-ink/15 bg-white/[0.02] font-serif text-ink focus:border-ink/40 focus:outline-none"
            autoFocus
            required
          />
          <input
            type="password"
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-ink/15 bg-white/[0.02] font-serif text-ink focus:border-ink/40 focus:outline-none"
            required
          />
          {needTotp && (
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit authentication code"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full px-4 py-3 rounded-xl border border-amber/40 bg-amber/[0.04] font-mono text-center text-lg tracking-[0.3em] text-ink focus:border-amber focus:outline-none"
              autoFocus
              required
            />
          )}
        </div>
        {needTotp && <p className="font-serif italic text-ink/50 text-[12px] text-center -mt-3">Enter the code from your authenticator app.</p>}

        {error && <p className="text-[12px] text-red-300 text-center">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="w-full py-3 rounded-full bg-ink text-void text-[11px] hover:bg-particle transition-colors disabled:opacity-40"
        >
          {busy ? 'Entering…' : 'Enter'}
        </button>
      </form>
      </div>
    </main>
  );
}
